import { useState } from "react";

/**
 * Generic HTML5 drag-and-drop Kanban board.
 * props:
 *  columns: [{ key, title, accent, subtitle }]
 *  itemsByColumn: { [key]: item[] }
 *  getId: (item) => string
 *  renderCard: (item) => node
 *  onMove: (itemId, toColumnKey) => void
 */
export function KanbanBoard({ columns, itemsByColumn, getId, renderCard, onMove }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  return (
    <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin items-start" data-testid="kanban-board">
      {columns.map((col) => {
        const items = itemsByColumn[col.key] || [];
        return (
          <div
            key={col.key}
            data-testid={`kanban-column-${col.key}`}
            onDragOver={(e) => { e.preventDefault(); setOverCol(col.key); }}
            onDragLeave={() => setOverCol((c) => (c === col.key ? null : c))}
            onDrop={(e) => {
              e.preventDefault();
              setOverCol(null);
              if (dragId) onMove(dragId, col.key);
              setDragId(null);
            }}
            className={`w-[19rem] flex-shrink-0 flex flex-col rounded-lg border bg-slate-50/70 transition-colors ${overCol === col.key ? "border-slate-400 bg-slate-100" : "border-slate-200"}`}
          >
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-200">
              <div className="flex items-center gap-2 min-w-0">
                <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: col.accent || "#94A3B8" }} />
                <span className="font-heading font-semibold text-sm text-slate-800 truncate">{col.title}</span>
                {col.info}
                <span className="text-xs font-semibold text-slate-400">{items.length}</span>
              </div>
              {col.subtitle && <span className="text-xs font-medium text-slate-500 font-mono">{col.subtitle}</span>}
            </div>
            <div className="flex flex-col gap-2.5 p-2.5 min-h-[120px] overflow-y-auto scrollbar-thin max-h-[calc(100vh-15rem)]">
              {items.map((item) => (
                <div
                  key={getId(item)}
                  draggable
                  onDragStart={() => setDragId(getId(item))}
                  onDragEnd={() => setDragId(null)}
                  className={`kanban-card-enter cursor-grab active:cursor-grabbing rounded-lg border border-slate-200 bg-white shadow-sm hover:shadow hover:border-slate-300 transition-all ${dragId === getId(item) ? "opacity-50" : ""}`}
                >
                  {renderCard(item)}
                </div>
              ))}
              {items.length === 0 && (
                <div className="text-xs text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-lg">
                  Drop here
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
